import { createElement, Fragment, ReactNode } from 'react'
import type { Primitive } from 'type-fest'
import { toReactProp } from '~/utils'
import CONSTS from './parserConsts'

export function parse(template: string | undefined, variables: Record<string, Primitive> = {}) {
	if (!template) return null
	const isChar = () => peek?.match(/[a-z]/i)
	const isTagStart = (char?: string) => (typeof char === 'string' ? lookahead(`<${char}`) : lookahead(/<[a-z]/, 2))
	const isTagEnd = (char?: string) => (typeof char === 'string' ? lookahead(`</${char}>`) : lookahead(/<\/[a-z]/, 3))
	const isMore = () => peek !== undefined
	const isBrackets = () => lookahead(/({{)|(}})/, 2)
	const isText = () => !isTagStart() && !isTagEnd() && peek !== undefined
	const isQuote = () => (peek ? ['"', "'"].includes(peek) : false)
	const isSpace = () => peek == ' '

	const input = template
	let pos = 0
	let peek: string | undefined = input[pos++]
	try {
		const res = content()
		assert(!peek, `unexpected input: ${peek}`)
		return typeof res === 'string' ? res : createElement(Fragment, null, res)
	} catch (e) {
		console.warn('ParserError', e)
		return template
	}

	function next() {
		const c = peek
		if (pos > input.length) throw new Error('Parser out of bounds')
		peek = input[pos++]
		return c
	}

	function split(d: string, cb: () => void) {
		for (;;) {
			cb()
			if (peek == d) next()
			else break
		}
	}

	function assert(cond: boolean, message: string) {
		if (!cond) {
			console.warn('Parser:', message, 'while parsing', template)
		}
	}

	function lookahead(match: string): boolean
	function lookahead(match: RegExp, len: number): boolean
	function lookahead(match: RegExp | string, len = 0) {
		const start = pos - 1
		if (typeof match === 'string') {
			len = match.length
			for (let i = 0; i < len; i++) {
				if (match[i] !== input[start + i]) return false
			}
			return true
		} else {
			const str = input.substring(start, start + len)
			if (str.length !== len) return false
			const result = input.substring(start, start + len).match(match) !== null
			return result
		}
	}

	function variable(): Primitive {
		let key = ''
		assert(isBrackets(), `Exptected {{, got ${peek}`)
		next()
		next() // Swallow brackets
		while (isText() && !isBrackets() && !isQuote()) {
			key += next()
		}
		assert(isBrackets(), `Exptected }}, got ${peek}`)
		next()
		next() // Swallow brackets
		key = key.trim()
		return variables[key] ?? CONSTS[key] ?? `{${key}}`
	}

	function text(cond?: () => boolean) {
		let str = ''
		while (isText()) {
			if (isBrackets()) {
				str += variable()?.toString()
			} else {
				const c = next()
				str += c
			}
			if (cond && cond()) break
		}
		return str
	}

	function elem() {
		let tag = ''
		const props: Record<string, Primitive> = { key: pos /* Prevent key warning */ }
		let children: ReactNode = null
		split('<', () => {
			while (isChar()) tag += next()

			if (tag.length == 0) return

			split(' ', () => {
				if (!isChar()) return
				let prop = ''
				let value: Primitive = ''
				while (isChar()) prop += next()

				if (peek == '=') {
					next() // Swallow =
					if (isBrackets()) {
						value = variable()
					} else {
						value = text(() => isQuote() || isSpace() || peek == '>')
					}
				} else {
					// Attributes without assignment operator (e.g <button disabled) should be set to true
					value = true
				}
				props[toReactProp(prop)] = value
			})
			assert((peek as string) == '>', `Expected >, not ${peek}`)
			next()
			children = content()
			assert(isTagEnd(tag), `Expected </${tag}>, not ${peek}`)
			while (peek !== '>') next() // Swallow all character until end of closing tag
			next() // Swallow >
		})
		if (tag.length == 0) return null
		return createElement(tag, props, children)
	}

	function content() {
		const nodes: React.ReactNode[] = []
		let i = 0
		while (isMore() && i++ < input.length) {
			if (isTagStart()) {
				next() // Swallow '<'
				nodes.push(elem())
			} else if (isText()) {
				nodes.push(text())
			} else {
				break
			}
		}
		return nodes.length == 1 ? nodes[0] : nodes
	}
}
